import { Injectable } from '@angular/core';

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface ObservabilityLogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  correlationId?: string;
  url?: string;
  data?: unknown;
  stack?: string;
}

export interface MetricEntry {
  timestamp: string;
  name: string;
  value: number;
  tags?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class ObservabilityService {
  private readonly maxLogs = 150;
  private readonly storageKey = 'diaspora-wallet-observability-logs';
  private logs: ObservabilityLogEntry[] = [];
  private metrics: MetricEntry[] = [];

  constructor() {
    this.loadPersistedLogs();
    this.capturePerformanceMetrics();
    this.info('ObservabilityService', 'Observability system initialized');
  }

  debug(category: string, message: string, data?: unknown): void {
    this.record('DEBUG', category, message, data);
  }

  info(category: string, message: string, data?: unknown): void {
    this.record('INFO', category, message, data);
  }

  warn(category: string, message: string, data?: unknown): void {
    this.record('WARN', category, message, data);
  }

  error(category: string, message: string, error?: unknown, data?: unknown): void {
    const stack = error instanceof Error ? error.stack : undefined;
    const errorDetails = error instanceof Error
      ? { name: error.name, message: error.message, stack: error.stack }
      : error;

    const mergedData = data ? { ...(typeof data === 'object' ? data : { meta: data }), errorDetails } : errorDetails;
    this.record('ERROR', category, message, mergedData, stack);
  }

  trackMetric(name: string, value: number, tags?: Record<string, string>): void {
    const entry: MetricEntry = {
      timestamp: new Date().toISOString(),
      name,
      value: Math.round(value * 100) / 100,
      tags
    };
    this.metrics.push(entry);
    if (this.metrics.length > 100) {
      this.metrics.shift();
    }
    this.debug('Metric', `${name}: ${value}ms`, tags);
  }

  getLogs(): ObservabilityLogEntry[] {
    return [...this.logs];
  }

  getMetrics(): MetricEntry[] {
    return [...this.metrics];
  }

  exportDiagnostics(): Record<string, unknown> {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const superappToken = typeof window !== 'undefined' ? localStorage.getItem('superapp-token') : null;
    const correlationId = this.getActiveCorrelationId();

    return {
      appName: 'fintech-diaspora-wallet-pwa',
      exportedAt: new Date().toISOString(),
      environment: {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        screen: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown'
      },
      session: {
        correlationId,
        hasDiasporaSessionToken: Boolean(token),
        tokenPreview: token ? `${token.slice(0, 10)}...` : null,
        hasSuperappToken: Boolean(superappToken),
        superappTokenPreview: superappToken ? `${superappToken.slice(0, 10)}...` : null,
        diagnosticsState: this.safeGetJson('h5-session-diagnostics'),
        lastCrash: this.safeGetJson('h5-last-crash')
      },
      metrics: this.metrics,
      recentLogs: this.logs.slice(-50)
    };
  }

  clearLogs(): void {
    this.logs = [];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.storageKey);
    }
  }

  private record(
    level: LogLevel,
    category: string,
    message: string,
    data?: unknown,
    stack?: string
  ): void {
    const entry: ObservabilityLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      correlationId: this.getActiveCorrelationId(),
      url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
      data,
      stack
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.persistLogs();
    this.logToConsole(entry);
  }

  private logToConsole(entry: ObservabilityLogEntry): void {
    const prefix = `[${entry.level}][${entry.category}]`;
    const msg = `${entry.timestamp.slice(11, 23)} ${prefix} ${entry.message}`;

    switch (entry.level) {
      case 'DEBUG':
        console.debug(msg, entry.data || '');
        break;
      case 'INFO':
        console.info(msg, entry.data || '');
        break;
      case 'WARN':
        console.warn(msg, entry.data || '');
        break;
      case 'ERROR':
        console.error(msg, entry.data || '', entry.stack || '');
        break;
    }
  }

  private getActiveCorrelationId(): string | undefined {
    if (typeof localStorage === 'undefined') {
      return undefined;
    }
    return localStorage.getItem('correlation-id') || undefined;
  }

  private persistLogs(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs.slice(-60)));
    } catch {
      // Storage quota reached, safely ignore
    }
  }

  private loadPersistedLogs(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) {
        this.logs = JSON.parse(raw);
      }
    } catch {
      this.logs = [];
    }
  }

  private capturePerformanceMetrics(): void {
    if (typeof window === 'undefined' || !window.performance) {
      return;
    }

    window.addEventListener('load', () => {
      setTimeout(() => {
        try {
          const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
          if (navEntries && navEntries.length > 0) {
            const nav = navEntries[0];
            this.trackMetric('perf_page_load_time_ms', nav.loadEventEnd - nav.startTime);
            this.trackMetric('perf_dom_interactive_ms', nav.domInteractive - nav.startTime);
            this.trackMetric('perf_ttfb_ms', nav.responseStart - nav.requestStart);
          }

          const paintEntries = performance.getEntriesByType('paint');
          paintEntries.forEach(entry => {
            if (entry.name === 'first-contentful-paint') {
              this.trackMetric('perf_first_contentful_paint_ms', entry.startTime);
            }
          });
        } catch {
          // Performance API error, ignore
        }
      }, 0);
    });
  }

  private safeGetJson(key: string): unknown {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const val = localStorage.getItem(key);
    if (!val) {
      return null;
    }
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
}

